import type { ImportFieldNode } from '@erp/rpc';
import type { SchemaFieldType } from '@erp/shared-types';
import { ARRAY_ITEM_FIELD_NAME } from '@erp/shared-utils';
import type { ParseResult } from './extendAISchemaParser';
import {
  isPlainObject,
  parseJsonObjectInput,
  toDisplayLabel,
} from './schemaParserShared';

function scalarFieldType(value: unknown): SchemaFieldType {
  switch (typeof value) {
    case 'string':
      return 'string';
    case 'boolean':
      return 'boolean';
    case 'number':
      return Number.isInteger(value) ? 'integer' : 'float';
    default:
      // null / undefined carry no type information — `json` accepts anything.
      return 'json';
  }
}

/**
 * Union-merge the keys of every object element so sparse samples (a key only
 * present on some lines) still produce a field. First non-null occurrence
 * wins the type; a `json` child inferred from null upgrades when a later
 * element shows the real type.
 */
function mergeObjectElements(
  elements: Record<string, unknown>[],
): ImportFieldNode[] {
  const childByName = new Map<string, ImportFieldNode>();
  for (const element of elements) {
    for (const [key, value] of Object.entries(element)) {
      const existing = childByName.get(key);
      if (!existing) {
        childByName.set(key, parseValueNode(key, value));
      } else if (existing.fieldType === 'json' && value !== null) {
        childByName.set(key, parseValueNode(key, value));
      }
    }
  }
  return [...childByName.values()];
}

function parseArrayValue(
  fieldName: string,
  values: unknown[],
): ImportFieldNode {
  const node: ImportFieldNode = {
    fieldName,
    fieldType: 'array',
    label: toDisplayLabel(fieldName),
    description: null,
    isRequired: false,
    children: [],
  };

  const objectElements = values.filter(isPlainObject);
  if (objectElements.length > 0 && objectElements.length === values.length) {
    node.children = mergeObjectElements(objectElements);
    return node;
  }

  // Scalar (or mixed) item shape: synthesize a single placeholder child from
  // the first non-null element, mirroring parseExtendAISchema's array-of-scalar
  // representation so the downstream builder emits a valid `items` schema.
  const firstNonNull = values.find(
    (value) => value !== null && value !== undefined,
  );
  if (firstNonNull !== undefined) {
    node.children = [parseValueNode(ARRAY_ITEM_FIELD_NAME, firstNonNull)];
  }
  return node;
}

function parseValueNode(fieldName: string, value: unknown): ImportFieldNode {
  if (Array.isArray(value)) {
    return parseArrayValue(fieldName, value);
  }
  if (isPlainObject(value)) {
    return {
      fieldName,
      fieldType: 'object',
      label: toDisplayLabel(fieldName),
      description: null,
      isRequired: false,
      children: Object.entries(value).map(([key, child]) =>
        parseValueNode(key, child),
      ),
    };
  }
  return {
    fieldName,
    fieldType: scalarFieldType(value),
    label: toDisplayLabel(fieldName),
    description: null,
    isRequired: false,
  };
}

/**
 * Parse a raw sample JSON payload (an example request/response body, not a
 * JSON Schema) into a tree of import nodes. Key names are preserved VERBATIM —
 * no snake_case → camelCase conversion — because consumers like the ERP Input
 * Submission Map use schema field names as the literal wire keys of the built
 * payload. Field types are inferred from the sample values; nothing is marked
 * required (a single sample can't distinguish required from optional).
 */
export function parseSamplePayload(rawJson: string): ParseResult {
  const input = parseJsonObjectInput(
    rawJson,
    'Paste a JSON payload to import.',
  );
  if (!input.ok) {
    return input;
  }

  const fields = Object.entries(input.value).map(([key, value]) =>
    parseValueNode(key, value),
  );
  if (fields.length === 0) {
    return { ok: false, error: 'Sample payload has no keys to import.' };
  }

  return { ok: true, fields };
}

/**
 * Like {@link parseSamplePayload}, but also accepts a top-level JSON *array* of
 * records — the shape a data-asset export usually has. Keys are unioned across
 * every element, so a field missing from the first record is still picked up,
 * which is the JSON equivalent of sampling several CSV rows.
 */
export function parseSampleRecords(rawJson: string): ParseResult {
  const trimmed = rawJson.trim();
  if (!trimmed) {
    return { ok: false, error: 'Paste a JSON payload to import.' };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    const detail = err instanceof Error ? err.message : 'unknown error';
    return { ok: false, error: `Invalid JSON: ${detail}` };
  }

  if (!Array.isArray(parsed)) {
    return parseSamplePayload(rawJson);
  }

  const elements = parsed.filter(isPlainObject);
  if (elements.length === 0) {
    return {
      ok: false,
      error: 'The JSON array has no object records to infer fields from.',
    };
  }

  const fields = mergeObjectElements(elements);
  if (fields.length === 0) {
    return { ok: false, error: 'Sample payload has no keys to import.' };
  }

  return { ok: true, fields };
}
